import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './../AuthContext';
import api from './../api.js';
import { IndianCurrencyFormatter, FormatDate } from './../Function';
import Swal from 'sweetalert2';

function CommissionHistory() {

    const { user, fulluser } = useContext(AuthContext);
    const navigate = useNavigate();
    const [transactionHistory, setTransactionHistory] = useState({
        commission_payments: [],
        admin_payments: [],
        bill_transactions: []
    });
    const [summary, setSummary] = useState(null);
    const [commission_by_payment, setCommissionByPayment] = useState(null);
    // unused: kept only to avoid changing API payload shape
    // eslint-disable-next-line no-unused-vars
    const [unpaidCommission, setUnpaidCommission] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
const [history, sethistory] = useState(null);
    const vendorId = useMemo(() => (fulluser?.id ?? user ?? null), [fulluser?.id, user]);

    const totalCommissionPayments = useMemo(() => {
        return (transactionHistory.commission_payments || []).reduce(
            (sum, item) => sum + (parseFloat(item.amount) || 0),
            0
        );
    }, [transactionHistory.commission_payments]);

    const totalAdminPayments = useMemo(() => {
        return (transactionHistory.admin_payments || []).reduce(
            (sum, item) => sum + (parseFloat(item.amount) || 0),
            0
        );
    }, [transactionHistory.admin_payments]);

    useEffect(() => {
        if (!vendorId) return;
        fetchTransactionHistory();
        checkCommissionAlerts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    const fetchTransactionHistory = async () => {
        try {
            if (!vendorId) return;
            const response = await api.get(`/vendor/transaction-history?vendor_id=${encodeURIComponent(String(vendorId))}`);
            if (response.data.status === 'success') {
                sethistory(response.data);
                setTransactionHistory(response.data.transactions);
                setSummary(response.data.summary);
                
                setCommissionByPayment(response.data.commission_by_payment);
                setUnpaidCommission(response.data?.summary?.pending_commission || 0);
            }
        } catch (error) {
            console.error('Error fetching transaction history:', error);
        }
    };

    const checkCommissionAlerts = async () => {
        try {
            if (!vendorId) return;
            const response = await api.post('/vendor/check-commission-alerts', {
                vendor_id: vendorId
            });
            if (response.data.show_alert) {
                setShowAlert(true);
                setAlertMessage(response.data.message);
            }
        } catch (error) {
            console.error('Error checking commission alerts:', error);
        }
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        try {
            await Promise.all([fetchTransactionHistory(), checkCommissionAlerts()]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePayCommission = async () => {
        if (!vendorId) {
            Swal.fire('Error', 'Vendor ID not found. Please login again.', 'error');
            return;
        }

        const pendingAmount = Math.abs(parseFloat(summary?.net_balance || 0)) || 0;
        if (pendingAmount <= 0) {
            Swal.fire('Info', 'No pending commission to pay', 'info');
            return;
        }
console.log(pendingAmount);
        const dueAmount = pendingAmount;

        const result = await Swal.fire({
            title: 'Pay Commission',
            text: `Pay ₹${dueAmount} amount to admin`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Pay Now',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                if (!window.Razorpay) {
                    Swal.fire('Error', 'Razorpay SDK not loaded on this page.', 'error');
                    return;
                }

                // 1) Create Razorpay order
                const orderResponse = await api.post('/vendor/create-commission-order', {
                    vendor_id: vendorId,
                    amount: dueAmount
                });

                if (orderResponse.data?.status !== 'success') {
                    throw new Error(orderResponse.data?.message || 'Failed to create payment order');
                }

                const { order_id, key, vendor_details } = orderResponse.data;

                // 2) Open Razorpay checkout
                const options = {
                    key: key,
                    amount: dueAmount * 100,
                    currency: 'INR',
                    name: 'APM Smart Hub',
                    description: `Commission Payment - ${vendor_details?.name || 'Vendor'}`,
                    order_id: order_id,
                    handler: async function (rzpResponse) {
                        try {
                            // 3) Verify payment with backend
                            const verifyResponse = await api.post('/vendor/verify-commission-payment', {
                                vendor_id: vendorId,
                                razorpay_order_id: order_id,
                                razorpay_payment_id: rzpResponse.razorpay_payment_id,
                                razorpay_signature: rzpResponse.razorpay_signature,
                                amount: dueAmount
                            });

                            if (verifyResponse.data?.status === 'success') {
                                Swal.fire('Success', verifyResponse.data?.message || 'Commission paid successfully', 'success');
                                await handleRefresh();
                                setShowAlert(false);
                            } else {
                                throw new Error(verifyResponse.data?.message || 'Payment verification failed');
                            }
                        } catch (error) {
                            Swal.fire(
                                'Payment Failed!',
                                error.response?.data?.message || error.message || 'Payment verification failed. Please contact support.',
                                'error'
                            );
                        }
                    },
                    prefill: {
                        name: vendor_details?.name,
                        email: vendor_details?.email,
                        contact: vendor_details?.contact
                    },
                    notes: {
                        vendor_id: String(vendorId),
                        type: 'commission_payment'
                    },
                    theme: { color: '#3399cc' },
                    modal: {
                        ondismiss: function () { }
                    }
                };

                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', function (response) {
                    Swal.fire(
                        'Payment Failed!',
                        response.error?.description || 'Payment was not completed. Please try again.',
                        'error'
                    );
                });
                rzp.open();
            } catch (error) {
                Swal.fire(
                    'Payment Failed!',
                    error.response?.data?.message || error.message || 'Failed to initiate payment. Please try again.',
                    'error'
                );
            }
        }
    };

    const handleTransactionClick = (item, type) => {
        if (type === 'bill_transactions' && item.billorder_id) {
            navigate(`/Payment?id=${item.billorder_id}`);
            return;
        }
        if (type === 'commission_payments' || type === 'admin_payments') {
            navigate('/TransactionReceipt', { state: { item, type, vendor: fulluser } });
        }
    };

    const renderTransactionItem = (item, type) => {
        const getIcon = () => {
            switch (type) {
                case 'commission_payments':
                    return 'arrow-up-circle';
                case 'admin_payments':
                    return 'arrow-down-circle';
                case 'bill_transactions':
                    return 'receipt-outline';
                default:
                    return 'cash-outline';
            }
        };

        const getIconColor = () => {
            switch (type) {
                case 'commission_payments':
                    return 'bg-danger';
                case 'admin_payments':
                    return 'bg-success';
                case 'bill_transactions':
                    return 'bg-primary';
                default:
                    return 'bg-secondary';
            }
        };

        const getAmountColor = () => {
            switch (type) {
                case 'commission_payments':
                    return 'text-danger';
                case 'admin_payments':
                    return 'text-success';
                case 'bill_transactions':
                    return 'text-primary';
                default:
                    return 'text-secondary';
            }
        };

        const getAmountPrefix = () => {
            switch (type) {
                case 'commission_payments':
                    return '→';
                case 'admin_payments':
                    return '←';
                case 'bill_transactions':
                    return item.direction === 'vendor to admin' ? '→' : '←';
                default:
                    return '';
            }
        };

        return (
            <li
                key={`${type}-${item.id ?? item.billorder_id ?? item.billno ?? item.created_at ?? Math.random()}`}
                style={{ display: 'block' }}
            >
                <div
                    className="item"
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleTransactionClick(item, type)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleTransactionClick(item, type);
                        }
                    }}
                >
                    <div className={`icon-box ${getIconColor()}`}>
                        <ion-icon name={getIcon()} role="img" className="md hydrated" />
                    </div>
                    <div className="in">
                        <div>
                            <strong>{item.description}</strong>
                            {item.billno && (
                                <div className="text-small text-secondary">
                                    Bill #{item.billno} • {item.paytype?.toUpperCase() || 'CASH'}
                                </div>
                            )}
                            {item.admin_commission_rate && (
                                <div className="text-small text-secondary">
                                    Commission: {item.admin_commission_rate}% (₹{item.admin_commission_amount})
                                </div>
                            )}
                            {item.vendor_receivable_amount && (
                                <div className="text-small text-secondary">
                                    Vendor Receivable: ₹{item.vendor_receivable_amount}
                                </div>
                            )}
                            <div className="text-small text-secondary">
                                Method: {item.payment_method || 'N/A'}
                            </div>
                           {item.direction && <div>
                              <strong>{(item.direction || 'N/A').replace(/_/g, ' ')}</strong>
                            </div>}
                        </div>
                        <div className="text-end">
                            <strong className={getAmountColor()}>
                                {getAmountPrefix()}<IndianCurrencyFormatter amount={item.amount} />
                            </strong>
                            {/* <div className="text-small">
                                <span className={`badge ${
                                    item.status === 'completed' || item.status === 'paid' ? 'bg-success' : 
                                    item.status === 'pending' ? 'bg-warning' : 'bg-secondary'
                                }`}>
                                    {item.status === 'completed' || item.status === 'paid' ? 'Completed' : 
                                     item.status === 'pending' ? 'Pending' : 'Unknown'}
                                </span>
                            </div> */}
                            <div className="text-small text-secondary">
                                {FormatDate(item.created_at)}
                            </div>
                            <div className="text-small text-primary mt-1">View receipt</div>
                        </div>
                    </div>
                </div>
            </li>
        );
    };

    return (
        <div>
            {/* Commission Alert Popup */}
            {showAlert && (
                <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert">
                    <strong>⚠️ Commission Payment Reminder</strong><br />
                    {alertMessage}
                    <button type="button" className="btn-close" onClick={() => setShowAlert(false)}></button>
                </div>
            )}

            {/* Summary Card */}
            <div className="section mb-4">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Transaction Summary</h5>
                        <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleRefresh}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                    <div className="card-body">
                        {summary && (
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Bills</h6>
                                            <h4 className="text-primary mb-0">{summary.total_bills}</h4>
                                            <small className="text-muted">{summary.period || 'Current'}</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Bill Amount</h6>
                                            <h4 className="text-info mb-0">₹{(parseFloat(summary.total_bill_amount) || 0).toFixed(2)}</h4>
                                            <small className="text-muted">{summary.period || 'Current'}</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Cancelled Bills</h6>
                                            <h4 className="text-info mb-0">{summary.cancelled_bill_count}</h4>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Cancelled Bill Amount</h6>
                                            <h4 className="text-info mb-0">₹{(parseFloat(summary.cancelled_bill_amount) || 0).toFixed(2)}</h4>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Commission</h6>
                                            <h4 className={`mb-0 ${history.payCommission >= 0 ? 'text-success' : 'text-danger'}`}>
                                                ₹{(parseFloat(history.payCommission) || 0).toFixed(2)}
                                            </h4>
                                            <small className="text-muted">{summary.period || 'Current'}</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Amt, vendor paid to Admin</h6>
                                            <h4 className={`mb-0 ${totalCommissionPayments >= 0 ? 'text-danger' : 'text-success'}`}>
                                                ₹{totalCommissionPayments.toFixed(2)}
                                            </h4>
                                        </div>

                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Amt, Admin paid to Vendor</h6>
                                            <h4 className={`mb-0 ${totalAdminPayments >= 0 ? 'text-success' : 'text-danger'}`}>
                                                ₹{totalAdminPayments.toFixed(2)}
                                            </h4>
                                        </div>

                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-light">
                                        <div className="card-body text-center">
                                            <h6 className="card-title mb-1">Total Reedemed Points</h6>
                                            <h4 className={`mb-0 ${summary.redeempoint >= 0 ? 'text-success' : 'text-danger'}`}>
                                                ₹{(parseFloat(summary.redeempoint) || 0).toFixed(2)}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                            <strong></strong>
                            <span className={`h4 mb-0 ${summary && summary.net_balance >= 0 ? 'text-success' : 'text-danger'}`}>
                                <IndianCurrencyFormatter amount={Math.abs(summary?.net_balance || 0)} />
                                {summary && summary.net_balance < 0 && ' Commission Due'}
                            </span>
                        </div>
                        {summary && parseFloat(summary.net_balance || 0) > 0 && (
                            <div className="alert alert-info mt-3 mb-0">
                                <h5 className="mb-2">
                                    <strong>Admin Pay to You (Net Payout):</strong>
                                    ₹{parseFloat(summary?.net_balance || 0).toFixed(2)}
                                </h5>
                                <div className="mb">
                                    <small className="text-dark">This is the net amount you will receive after all payments:</small>
                                </div>
                                <div className="row">
                                    <div className="col-md-4">
                                        <small className="text-dark">• From Cash Payments: ₹{(parseFloat(commission_by_payment.vendor_receivable_cash) || 0).toFixed(2)}</small>
                                    </div>
                                    <div className="col-md-4">
                                        <small className="text-dark">• From Customer Rewards: ₹{(parseFloat(summary.billorder_total_reward_used) || 0).toFixed(2)}</small>
                                    </div>
                                    <div className="col-md-4">
                                        <small className="text-dark">• From Online Payments: ₹{(parseFloat(commission_by_payment.vendor_receivable_online) || 0).toFixed(2)}</small>
                                    </div>
                                    
                                </div>
                            </div>
                        )}
                        {summary && parseFloat(summary.net_balance || 0) < 0 && (
                            <div className="alert alert-warning mt-3 mb-0">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Vendor pays admin:</strong><br />
                                        <small>Collection due. Please clear to avoid restrictions.</small>
                                    </div>
                                    <button
                                        className="btn btn-warning btn-sm"
                                        onClick={handlePayCommission}
                                    >
                                        Pay ₹{(parseFloat(Math.abs(summary?.net_balance || 0)) || 0).toFixed(2)}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Commission Payments (Vendor to Admin) */}
            {transactionHistory.commission_payments.length > 0 && (
                <div className="section mb-4">
                    <div className="section-heading">
                        <h2 className="title">Commission Payments (You → Admin)</h2>
                    </div>
                    <div className="card">
                        <ul className="listview flush transparent no-line image-listview detailed-list mt-1 mb-1">
                            {transactionHistory.commission_payments.map((item) => 
                                renderTransactionItem(item, 'commission_payments')
                            )}
                        </ul>
                    </div>
                </div>
            )}

            {/* Admin Payments (Admin to Vendor) */}
            {transactionHistory.admin_payments.length > 0 && (
                <div className="section mb-4">
                    <div className="section-heading">
                        <h2 className="title">Admin Payments (Admin → You)</h2>
                    </div>
                    <div className="card">
                        <ul className="listview flush transparent no-line image-listview detailed-list mt-1 mb-1">
                            {transactionHistory.admin_payments.map((item) => 
                                renderTransactionItem(item, 'admin_payments')
                            )}
                        </ul>
                    </div>
                </div>
            )}

            {/* Bill Transactions */}
            {transactionHistory.bill_transactions.length > 0 && (
                <div className="section mb-4">
                    <div className="section-heading">
                        <h2 className="title">Bill Transactions</h2>
                    </div>
                    <div className="card">
                        <ul className="listview flush transparent no-line image-listview detailed-list mt-1 mb-1">
                            {transactionHistory.bill_transactions.map((item) => 
                                renderTransactionItem(item, 'bill_transactions')
                            )}
                        </ul>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {transactionHistory.commission_payments.length === 0 && 
             transactionHistory.admin_payments.length === 0 && 
             transactionHistory.bill_transactions.length === 0 && (
                <div className="section">
                    <div className="card">
                        <div className="card-body text-center text-muted py-5">
                            <ion-icon name="receipt-outline" style={{ fontSize: '4rem', opacity: 0.3 }}></ion-icon>
                            <h5 className="mt-3">No Transactions Yet</h5>
                            <p>Your transaction history will appear here once you start making bills and payments</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CommissionHistory
